import { useState } from "react";
import Avatar from "../../../../../../components/Avatar";
import { motion } from "motion/react";
import Button from "../../../../../../components/Button";
import { BsFillSendPlusFill } from "react-icons/bs";
import CreateDocumentModal from "../../../../../../components/CreateDocumentModal";
import {
  CreateDocumentSharingDocument,
  DocumentFragment,
  DocumentsByUserOrganizationIdDocument,
  InviteUserOrganizationDocument,
  SharingInvitationsDocument,
  SharingMode,
} from "../../../../../../__generated__/graphql";
import { useMutation } from "@apollo/client";
import usePublicUserOrganization from "./hooks/usePublicUserOrganization";
import { organizationTypeToName } from "../../../../../../lib/utils";
import { toast } from "react-hot-toast";
import useOrganizationSharings from "../../../../../../hooks/useOrganizationSharings";
import useCurrentOrgInvitations from "../../../../components/InvitationsList/hooks/useCurrentOrgInvitations";
import { FaCheckCircle } from "react-icons/fa";
import useOrganizationMembers from "../../../../../../hooks/useOrganizationMembers";

interface CollaboratorHeaderProps {
  userOrganizationId?: string;
}

export default function CollaboratorHeader({
  userOrganizationId,
}: CollaboratorHeaderProps) {
  const [isCreateDocumentModalOpen, setIsCreateDocumentModalOpen] =
    useState(false);

  const [createDocumentSharing] = useMutation(CreateDocumentSharingDocument);

  const {
    data: publicUserOrganization,
    loading: publicUserOrganizationLoading,
  } = usePublicUserOrganization(userOrganizationId);
  const [inviteUserOrganization, { loading: inviteUserOrganizationLoading }] =
    useMutation(InviteUserOrganizationDocument, {
      onCompleted: () => {
        toast.success("Invitation envoyée avec succès");
      },
      onError: (error) => {
        toast.error(error.message);
      },
    });

  const { data: organizationSharings, loading: organizationSharingsLoading } =
    useOrganizationSharings();
  const { data: sharingInvitations, loading: sharingInvitationsLoading } =
    useCurrentOrgInvitations();
  const { data: organizationMembers, loading: organizationMembersLoading } =
    useOrganizationMembers();

  const userOrganizationAlreadyAdded =
    organizationSharings?.organizationSharings?.find((organizationSharing) =>
      organizationSharing?.assignedPublicUserOrganizations?.find(
        (publicUserOrganization) =>
          publicUserOrganization?.id === userOrganizationId
      )
    );

  const userOrganizationAlreadyInvited =
    !userOrganizationAlreadyAdded &&
    sharingInvitations?.sharingInvitations?.find(
      (invitation) =>
        invitation?.recipientUserOrganizationId === userOrganizationId
    );

  const userAlreadyInOrganization =
    organizationMembers?.organizationMembers?.find(
      (member) => member?.id === userOrganizationId
    );

  const isLoading =
    publicUserOrganizationLoading ||
    organizationSharingsLoading ||
    sharingInvitationsLoading ||
    organizationMembersLoading;

  // TODO: implement loading state
  if (isLoading) return null;

  if (!publicUserOrganization || !userOrganizationId) {
    return <div>Pas d'informations trouvées</div>;
  }

  const handleCreateDocument = async (document: DocumentFragment) => {
    if (
      !publicUserOrganization ||
      !publicUserOrganization.organizationId ||
      !userOrganizationId
    ) {
      return;
    }

    await createDocumentSharing({
      variables: {
        createDocumentSharingInput: {
          documentId: document.id,
          recipientOrganizationId: publicUserOrganization.organizationId,
          recipientUserOrganizationId: userOrganizationId,
          sharingMode: SharingMode.Sent,
        },
      },
      refetchQueries: [
        {
          query: DocumentsByUserOrganizationIdDocument,
          variables: {
            userOrganizationId: userOrganizationId,
          },
        },
      ],
    });

    setIsCreateDocumentModalOpen(false);
  };

  const userTitle = () => {
    return (
      <div className="flex gap-4 pr-8">
        <Avatar
          userOrganization={publicUserOrganization}
          shape="square"
          size="xlarge"
        />
        <div className="flex flex-col">
          <div className="text-2xl font-bold">
            {publicUserOrganization.name}
          </div>
          <div className="text-sm text-accent uppercase">
            {publicUserOrganization.jobTitle ??
              organizationTypeToName[publicUserOrganization.organizationType]}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <CreateDocumentModal
        isOpen={isCreateDocumentModalOpen}
        onClose={() => setIsCreateDocumentModalOpen(false)}
        onSubmit={handleCreateDocument}
      />

      <motion.div
        className="flex justify-between"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-start gap-8">{userTitle()}</div>

        {userOrganizationAlreadyAdded ? (
          <Button
            variant="secondary"
            className="flex gap-2"
            onClick={() => setIsCreateDocumentModalOpen(true)}
          >
            <BsFillSendPlusFill />
            Envoyer un document
          </Button>
        ) : userOrganizationAlreadyInvited ? (
          <div className="text-sm text-secondary flex gap-2 items-center">
            <FaCheckCircle className="w-4 h-4" />
            Invitation envoyée
          </div>
        ) : userAlreadyInOrganization ? null : (
          <Button
            isLoading={inviteUserOrganizationLoading}
            variant="secondary"
            className="flex gap-2 border border-accent-glow"
            onClick={() => {
              inviteUserOrganization({
                variables: {
                  userOrganizationId,
                },
                refetchQueries: [SharingInvitationsDocument],
              });
            }}
          >
            Ajouter à votre réseau
            <div className="text-sm font-bold text-primary bg-dark rounded-md w-6 h-6 flex items-center justify-center border border-border">
              K
            </div>
          </Button>
        )}
      </motion.div>
    </>
  );
}
